/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 下载已编辑照片选择弹窗组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10
 */

import React, { useState, useMemo, useEffect } from 'react'
import { Modal, Button, Checkbox, message } from 'antd'
import {
  PhotoItem,
  WatermarkExtractionStatus
} from '../../context/WatermarkBatchContext'
import useImageProcessUrl from '../../hooks/useImageProcessUrl'
import './DownloadEditedPhotosModal.scss'
import { PictureOutlined } from '@ant-design/icons'

interface DownloadEditedPhotosModalProps {
  visible: boolean
  photos: PhotoItem[]
  confirmLoading?: boolean
  onCancel: () => void
  onConfirm: (selectedPhotos: PhotoItem[]) => void
}

const DownloadEditedPhotosModal: React.FC<DownloadEditedPhotosModalProps> = ({
  visible,
  photos,
  confirmLoading = false,
  onCancel,
  onConfirm
}) => {
  // 获取已处理的照片（编辑成功 + 无修改项 + 超时）
  const processedPhotos = useMemo(() => {
    return photos.filter(
      (photo) =>
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED ||
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES ||
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT
    )
  }, [photos])

  // 选中的照片 seq 集合
  const [selectedSeqs, setSelectedSeqs] = useState<Set<number>>(new Set())
  // 全选状态
  const [checkAll, setCheckAll] = useState(false)
  const { buildThumbUrl } = useImageProcessUrl()

  const THUMB_SIZE = 147

  // 初始化：默认全选
  useEffect(() => {
    if (visible && processedPhotos.length > 0) {
      const allSeqs = new Set(processedPhotos.map((photo) => photo.seq))
      setSelectedSeqs(allSeqs)
      setCheckAll(true)
    } else {
      setSelectedSeqs(new Set())
      setCheckAll(false)
    }
  }, [visible, processedPhotos])

  const handlePhotoToggle = (seq: number) => {
    const newSelectedSeqs = new Set(selectedSeqs)
    if (newSelectedSeqs.has(seq)) {
      newSelectedSeqs.delete(seq)
    } else {
      newSelectedSeqs.add(seq)
    }
    setSelectedSeqs(newSelectedSeqs)
    setCheckAll(newSelectedSeqs.size === processedPhotos.length)
  }

  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      const allSeqs = new Set(processedPhotos.map((photo) => photo.seq))
      setSelectedSeqs(allSeqs)
      setCheckAll(true)
    } else {
      setSelectedSeqs(new Set())
      setCheckAll(false)
    }
  }

  const handleConfirm = () => {
    if (selectedSeqs.size === 0) {
      message.warning('请至少选择一张照片')
      return
    }

    const selectedPhotos = processedPhotos.filter((photo) =>
      selectedSeqs.has(photo.seq)
    )
    onConfirm(selectedPhotos)
  }

  const getPhotoPreviewUrl = (photo: PhotoItem): string => {
    const shouldUseOriginal =
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES ||
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT
    const rawUrl = shouldUseOriginal
      ? photo.smallURL || photo.url
      : photo.aiURL || photo.smallURL || photo.url
    return buildThumbUrl(rawUrl, THUMB_SIZE)
  }

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="选择要下载的已编辑照片"
      width={820}
      className="download-edited-photos-modal"
      styles={{ body: { padding: 0 } }}
      footer={
        <div className="download-edited-photos-modal-footer">
          <div className="footer-info">
            已选择 {selectedSeqs.size} / {processedPhotos.length} 张
          </div>
          <div className="footer-actions">
            <Button onClick={onCancel} className="cancel-btn">
              取消
            </Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              loading={confirmLoading}
              disabled={confirmLoading}
              className="confirm-btn"
            >
              开始下载
            </Button>
          </div>
        </div>
      }
    >
      <div className="download-edited-photos-modal-content">
        <div className="check-all-wrapper">
          <Checkbox
            checked={checkAll}
            onChange={(e) => handleCheckAll(e.target.checked)}
            className="check-all-checkbox"
          >
            全选 ({processedPhotos.length} 张)
          </Checkbox>
        </div>

        <div className="photos-grid">
          {processedPhotos.map((photo) => {
            const isSelected = selectedSeqs.has(photo.seq)
            const imageUrl = getPhotoPreviewUrl(photo)

            return (
              <div
                key={photo.seq}
                className={`photo-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handlePhotoToggle(photo.seq)}
              >
                <div className="photo-thumbnail">
                  <img
                    src={imageUrl}
                    alt={`已编辑照片 ${photo.seq}`}
                    className="thumbnail-image"
                  />
                  <div className="photoCheckbox">
                    <Checkbox
                      checked={isSelected}
                      className="photo-item-checkbox"
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {processedPhotos.length === 0 && (
          <div className="empty-state">
            <PictureOutlined className="empty-icon" />
            <div className="empty-text">暂无已编辑的照片</div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default DownloadEditedPhotosModal
